import React, { useEffect, useMemo } from "react";

import {
  Textfield,
  CheckBox,
  MultipleSelect,
  ErrorMessage,
  SingleSelect,
} from "widgets";
import { useFetchPostalCodeDetails } from "store/backend/apiHooks";
import { INDIA_POSTAL_CODE_LENGTH } from "constants/schoolConstants";
import {
  SELECT_OPTION_FALLBACK_LABEL,
  MAX_TAX_REGISTRATION_NUMBER_LENGTH,
  ZERO,
} from "constants/constants";
import {
  findOptionById,
  mapToSelectOptions,
  updateTenantCountrySelection,
  updateContactIsdCode,
  fillAddressFromPostalCode,
  clearStateAndDistrictWhenPinIncomplete,
  getAddressFormFlagsForCountry,
  updateContactPhoneFromInput,
  normalizePinCodeInput,
} from "helpers/tenantUtils";
import { shouldShowFormError } from "utils/utils";
import Loader from "shared/Icons/Loader/Loader";

import styles from "./TenantForm.module.scss";

const TenantForm = (props) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched = () => {},
    handleBlur = () => {},
    verticalOptions,
    gradeOptions,
    countryOptions = [],
    isVerticalOrGradeListLoading,
    isCountryListLoading,
    isErrorOnVerticalList,
    isErrorOnGradeList,
    isErrorOnCountryList,
    errorMessageOnFetchVerticalList,
    errorMessageOnFetchGradeList,
    errorMessageOnFetchCountryList,
    disableHasSingleBranchEdit,
    submitCount = ZERO,
    dirty = false,
  } = props;

  const submitAttempted = submitCount > ZERO;
  const showSelectFieldErrors = submitAttempted || dirty;

  const address = values.address || {};
  const contact = values.contact || {};
  const selectedCountry = findOptionById(countryOptions, values.countryId);
  const currencyOptions = useMemo(
    () =>
      mapToSelectOptions(
        selectedCountry?.currencies,
        SELECT_OPTION_FALLBACK_LABEL.CURRENCY,
      ),
    [selectedCountry],
  );
  const timezoneOptions = useMemo(
    () =>
      mapToSelectOptions(
        selectedCountry?.timezones,
        SELECT_OPTION_FALLBACK_LABEL.TIMEZONE,
      ),
    [selectedCountry],
  );
  const isdCodeOptions = useMemo(
    () =>
      mapToSelectOptions(
        selectedCountry?.isdCodes,
        SELECT_OPTION_FALLBACK_LABEL.ISD,
      ),
    [selectedCountry],
  );
  const {
    isIndia,
    isAreaRequired,
    isAreaDisabled,
    isStateDistrictReadOnly,
    showTaxRegistrationNumber,
    isCountryDerivedFieldDisabled,
  } = getAddressFormFlagsForCountry(selectedCountry);
  const pinCode = String(address.pinCode || "");
  const {
    data: postalCodeDetails,
    isFetching: isPostalCodeDetailsFetching,
    isPending: isPostalCodeDetailsLoading,
    isError: isErrorOnPostalCodeDetails,
  } = useFetchPostalCodeDetails(
    isIndia && pinCode.length === INDIA_POSTAL_CODE_LENGTH ? pinCode : undefined,
  );


  useEffect(() => {
    if (!isIndia || pinCode.length !== INDIA_POSTAL_CODE_LENGTH) return;
    fillAddressFromPostalCode(setFieldValue, postalCodeDetails);
  }, [postalCodeDetails, pinCode, isIndia, setFieldValue]);

  const showVerticalFetchError =
    (touched.verticalId || showSelectFieldErrors) &&
    isErrorOnVerticalList &&
    !errors.verticalId;
  const showVerticalYupError =
    (touched.verticalId || showSelectFieldErrors) && errors.verticalId;
  const showGradeFetchError =
    (touched.gradeIds || showSelectFieldErrors) &&
    isErrorOnGradeList &&
    !errors.gradeIds;
  const showGradeYupError =
    (touched.gradeIds || showSelectFieldErrors) && errors.gradeIds;
  const showCountryFetchError =
    (touched.countryId || showSelectFieldErrors) &&
    isErrorOnCountryList &&
    !errors.countryId;
  const showCountryYupError =
    (touched.countryId || showSelectFieldErrors) && errors.countryId;
  const showCurrencyYupError =
    (touched.currencyIds || submitAttempted) && errors.currencyIds;
  const showTimezoneYupError =
    (touched.timezoneIds || submitAttempted) && errors.timezoneIds;
  const contactTouched = touched.contact || {};
  const contactErrors = errors.contact || {};
  const showIsdCodeYupError = shouldShowFormError(
    contactTouched.isdCode,
    contactErrors.isdCode,
    submitAttempted,
  );
  const showPhoneYupError = shouldShowFormError(
    contactTouched.phone,
    contactErrors.phone,
    submitAttempted,
  );

  return (
    <div className={styles.formRoot}>
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Tenant information</h2>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <Textfield
              name="name"
              label="Tenant name *"
              value={values.name}
              handleOnChange={(e) => setFieldValue("name", e.target.value, true)}
              onBlur={handleBlur}
            />
            {touched.name && errors.name ? (
              <ErrorMessage>{errors.name}</ErrorMessage>
            ) : null}
          </div>
          <div className={styles.field}>
            {isVerticalOrGradeListLoading ? (
              <Loader />
            ) : (
              <SingleSelect
                name="verticalId"
                tag="Vertical *"
                menuItems={verticalOptions}
                value={values.verticalId}
                onBlur={handleBlur("verticalId")}
                handleChange={(e) => {
                  setFieldValue("verticalId", e.target.value, true);
                  setFieldTouched("verticalId", true, false);
                }}
              />
            )}
            {showVerticalYupError ? (
              <ErrorMessage>{errors.verticalId}</ErrorMessage>
            ) : showVerticalFetchError ? (
              <ErrorMessage>
                {errorMessageOnFetchVerticalList?.message ||
                  "Failed to load verticals"}
              </ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            {isCountryListLoading ? (
              <Loader />
            ) : (
              <SingleSelect
                name="countryId"
                tag="Country *"
                menuItems={countryOptions}
                value={values.countryId}
                onBlur={handleBlur("countryId")}
                handleChange={(e) => {
                  updateTenantCountrySelection({
                    setFieldValue,
                    setFieldTouched,
                    previousCountryId: values.countryId,
                    nextCountryId: e.target.value,
                    countryOptions,
                  });
                }}
              />
            )}
            {showCountryYupError ? (
              <ErrorMessage>{errors.countryId}</ErrorMessage>
            ) : showCountryFetchError ? (
              <ErrorMessage>
                {errorMessageOnFetchCountryList?.message ||
                  "Failed to load countries"}
              </ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            <MultipleSelect
              name="currencyIds"
              tag="Currency *"
              menuItems={currencyOptions}
              value={values.currencyIds}
              disabled={isCountryDerivedFieldDisabled}
              maxVisibleChips={2}
              onBlur={handleBlur("currencyIds")}
              handleChange={(e) => {
                setFieldValue("currencyIds", e.target.value, true);
                setFieldTouched("currencyIds", true, false);
              }}
            />
            {showCurrencyYupError ? (
              <ErrorMessage>{errors.currencyIds}</ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            <MultipleSelect
              name="timezoneIds"
              tag="Timezone *"
              menuItems={timezoneOptions}
              value={values.timezoneIds}
              disabled={isCountryDerivedFieldDisabled}
              maxVisibleChips={2}
              onBlur={handleBlur("timezoneIds")}
              handleChange={(e) => {
                setFieldValue("timezoneIds", e.target.value, true);
                setFieldTouched("timezoneIds", true, false);
              }}
            />
            {showTimezoneYupError ? (
              <ErrorMessage>{errors.timezoneIds}</ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            <Textfield
              name="code"
              label="Tenant code *"
              value={values.code}
              handleOnChange={(e) => setFieldValue("code", e.target.value, true)}
              onBlur={handleBlur}
            />
            {touched.code && errors.code ? (
              <ErrorMessage>{errors.code}</ErrorMessage>
            ) : null}
          </div>
          <div className={styles.field}>
            <Textfield
              name="logo"
              label="Logo URL"
              value={values.logo}
              handleOnChange={(e) => setFieldValue("logo", e.target.value, true)}
              onBlur={handleBlur}
            />
          </div>

          <div className={styles.field}>
            <Textfield
              name="email"
              label="Email *"
              value={values.email}
              handleOnChange={(e) =>
                setFieldValue("email", e.target.value, true)
              }
              onBlur={handleBlur}
            />
            {touched.email && errors.email ? (
              <ErrorMessage>{errors.email}</ErrorMessage>
            ) : null}
          </div>
          {showTaxRegistrationNumber ? (
            <div className={styles.field}>
              <Textfield
                name="taxRegistrationNumber"
                label="Tax Registration Number"
                value={values.taxRegistrationNumber ?? ""}
                handleOnChange={(e) =>
                  setFieldValue("taxRegistrationNumber", e.target.value, true)
                }
                onBlur={handleBlur}
                inputProps={{ maxLength: MAX_TAX_REGISTRATION_NUMBER_LENGTH }}
              />
              {touched.taxRegistrationNumber && errors.taxRegistrationNumber ? (
                <ErrorMessage>{errors.taxRegistrationNumber}</ErrorMessage>
              ) : null}
            </div>
          ) : (
            <div className={styles.field}>
              <Textfield
                name="gstin"
                label="GSTIN"
                value={values.gstin ?? ""}
                handleOnChange={(e) =>
                  setFieldValue("gstin", e.target.value, true)
                }
                onBlur={handleBlur}
              />
              {touched.gstin && errors.gstin ? (
                <ErrorMessage>{errors.gstin}</ErrorMessage>
              ) : null}
            </div>
          )}

          <div className={styles.field}>
            <Textfield
              name="ambassadorMail"
              label="Ambassador email"
              value={values.ambassadorMail}
              handleOnChange={(e) =>
                setFieldValue("ambassadorMail", e.target.value, true)
              }
              onBlur={handleBlur}
            />
            {touched.ambassadorMail && errors.ambassadorMail ? (
              <ErrorMessage>{errors.ambassadorMail}</ErrorMessage>
            ) : null}
          </div>
          <div className={styles.field}>
            {isVerticalOrGradeListLoading ? (
              <Loader />
            ) : (
              <div className={styles.gradesSelectWrap}>
                <MultipleSelect
                  name="gradeIds"
                  tag="Grades *"
                  maxVisibleChips={3}
                  showSelectAllOption
                  selectAllLabel="Select all grades"
                  onBlur={handleBlur("gradeIds")}
                  menuItems={gradeOptions.map((option) => ({
                    ...option,
                    disabled: values.lockedGradeIds?.includes(option.value),
                  }))}
                  value={values.gradeIds}
                  handleChange={(e) => {
                    const selected = e.target.value;
                    const removed = values.lockedGradeIds?.some(
                      (id) => !selected.includes(id)
                    );
                    if (removed) {
                      return;
                    }
                    setFieldValue("gradeIds", selected, true);
                    setFieldTouched("gradeIds", true, false);
                  }}
                />
              </div>
            )}
            {showGradeYupError ? (
              <ErrorMessage>{errors.gradeIds}</ErrorMessage>
            ) : showGradeFetchError ? (
              <ErrorMessage>
                {errorMessageOnFetchGradeList?.message ||
                  "Failed to load grades"}
              </ErrorMessage>
            ) : null}
          </div>

          <div className={`${styles.field} ${styles.fieldFull}`}>
              <div className={styles.formCheckboxItem}>
                <CheckBox
                  title="Profile separate"
                  checked={values.profileSeparate}
                  handleCheck={(e) =>
                    setFieldValue("profileSeparate", e.target.checked, true)
                  }
                />
              </div>
              {disableHasSingleBranchEdit ? null : (
                <div className={styles.formCheckboxItem}>
                  <CheckBox
                    title="Has single branch"
                    checked={values.hasSingleBranch}
                    handleCheck={(e) =>
                      setFieldValue("hasSingleBranch", e.target.checked, true)
                    }
                  />
                </div>
              )}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Address</h2>
        <div className={styles.formGrid}>
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <Textfield
              name="address.addressLine1"
              label="Address line 1 *"
              value={address.addressLine1}
              handleOnChange={(e) =>
                setFieldValue("address.addressLine1", e.target.value, true)
              }
              onBlur={handleBlur}
            />
            {touched.address?.addressLine1 && errors.address?.addressLine1 ? (
              <ErrorMessage>{errors.address.addressLine1}</ErrorMessage>
            ) : null}
          </div>

          <div className={`${styles.field} ${styles.fieldFull}`}>
            <Textfield
              name="address.addressLine2"
              label="Address line 2"
              value={address.addressLine2}
              handleOnChange={(e) =>
                setFieldValue("address.addressLine2", e.target.value, true)
              }
              onBlur={handleBlur}
            />
          </div>
          <div className={styles.field}>
            <Textfield
              name="address.street"
              label="Street"
              value={address.street}
              handleOnChange={(e) =>
                setFieldValue("address.street", e.target.value, true)
              }
              onBlur={handleBlur}
            />
          </div>

          <div className={styles.field}>
            <Textfield
              name="address.city"
              label="City *"
              value={address.city}
              handleOnChange={(e) =>
                setFieldValue("address.city", e.target.value, true)
              }
              onBlur={handleBlur}
            />
            {touched.address?.city && errors.address?.city ? (
              <ErrorMessage>{errors.address.city}</ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            {isIndia && isPostalCodeDetailsFetching && isPostalCodeDetailsLoading ? (
              <Loader />
            ) : (
              <Textfield
                name="address.district"
                label={isIndia ? "District *" : "District"}
                value={address.district || ""}
                disabled={isStateDistrictReadOnly}
                handleOnChange={(e) =>
                  setFieldValue("address.district", e.target.value, true)
                }
                onBlur={handleBlur}
              />
            )}
            {touched.address?.district && errors.address?.district ? (
              <ErrorMessage>{errors.address.district}</ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            {isIndia && isPostalCodeDetailsFetching && isPostalCodeDetailsLoading ? (
              <Loader />
            ) : (
              <Textfield
                name="address.state"
                label={isIndia ? "State *" : "State"}
                value={address.state || ""}
                disabled={isStateDistrictReadOnly}
                handleOnChange={(e) =>
                  setFieldValue("address.state", e.target.value, true)
                }
                onBlur={handleBlur}
              />
            )}
            {touched.address?.state && errors.address?.state ? (
              <ErrorMessage>{errors.address.state}</ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            <Textfield
              name="address.pinCode"
              label="PIN code *"
              value={address?.pinCode || ""}
              handleOnChange={(e) => {
                const newValue = normalizePinCodeInput(e.target.value, isIndia);
                setFieldValue("address.pinCode", newValue, true);
                setFieldTouched("address.pinCode", true, false);
                clearStateAndDistrictWhenPinIncomplete(
                  setFieldValue,
                  isIndia,
                  newValue,
                );
              }}
              onBlur={handleBlur}
            />
            {touched.address?.pinCode && errors?.address?.pinCode ? (
              <ErrorMessage>{errors.address.pinCode}</ErrorMessage>
            ) : isIndia &&
              touched.address?.pinCode &&
              isErrorOnPostalCodeDetails ? (
              <ErrorMessage>Error fetching PIN code details</ErrorMessage>
            ) : null}
          </div>

          <div className={styles.field}>
            <Textfield
              name="address.area"
              label={isAreaRequired ? "Area *" : "Area"}
              value={address.area || ""}
              disabled={isAreaDisabled}
              handleOnChange={(e) =>
                setFieldValue("address.area", e.target.value, true)
              }
              onBlur={handleBlur}
            />
            {touched.address?.area && errors.address?.area ? (
              <ErrorMessage>{errors.address.area}</ErrorMessage>
            ) : null}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Contact</h2>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <Textfield
              name="contact.name"
              label="Contact name"
              value={contact.name}
              handleOnChange={(e) =>
                setFieldValue("contact.name", e.target.value, true)
              }
              onBlur={handleBlur}
            />
          </div>
          <div className={styles.field}>
            <Textfield
              name="contact.designation"
              label="Designation"
              value={contact.designation}
              handleOnChange={(e) =>
                setFieldValue("contact.designation", e.target.value, true)
              }
              onBlur={handleBlur}
            />
          </div>
          <div className={styles.field}>
            <SingleSelect
              name="contact.isdCode"
              tag="ISD code"
              menuItems={isdCodeOptions}
              value={contact.isdCode}
              disabled={isCountryDerivedFieldDisabled}
              clearable
              onBlur={handleBlur("contact.isdCode")}
              handleChange={(e) => {
                updateContactIsdCode({
                  setFieldValue,
                  setFieldTouched,
                  nextIsdCode: e.target.value,
                });
              }}
            />
            {showIsdCodeYupError ? (
              <ErrorMessage>{contactErrors.isdCode}</ErrorMessage>
            ) : null}
          </div>
          <div className={styles.field}>
            <Textfield
              name="contact.phone"
              label="Phone number"
              value={contact.phone}
              handleOnChange={(e) =>
                updateContactPhoneFromInput(e, setFieldValue, setFieldTouched)
              }
              onBlur={handleBlur}
            />
            {showPhoneYupError ? (
              <ErrorMessage>{contactErrors.phone}</ErrorMessage>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TenantForm;
